import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { LIFE_STAGE_LANES } from '../../data/lifeStageLanes';

/**
 * LifeStageLanesSection - v2 (warm redesign)
 *
 * Homepage tile band that lets a visitor self-identify with their
 * current moment in the home-ownership journey.
 *
 * Redesign notes (2026-02-28):
 * - Image-forward cards using real BuildPilot hero photography.
 *   Zero AI-illustrated art, zero pastel gradients, zero corner
 *   age chips (which the founder called out as "random").
 * - The age band is now a soft whisper line woven into the copy
 *   ("Usually late-teens through twenties") not a hard bracket.
 * - Warm off-white section background (#faf7f2) sits between the
 *   pure-white hero and the rest of the page, so the block reads
 *   as a warmer editorial moment. Palette otherwise still locked
 *   to BUILD_TEMPLATE §15.3 (navy #0a2540, blue #1d8efe, ink
 *   #1d1d1f, quiet #86868b, hairline #e8ebef).
 * - Mobile: single column of tall, image-led cards with the story
 *   sitting under the image. Feels closer to a real magazine
 *   layout than the previous cramped 2-column grid.
 * - Ready-to-build tile still opens the MiniMatcher directly so the
 *   mental model matches ShortlistStartersSection.
 *
 * No emails, no marketing hooks - pure content-routing surface.
 */

const LifeStageCard = ({ lane }) => {
  const isReady = !!lane.ctaOpensMatcher;

  const handleClick = (e) => {
    if (isReady) {
      e.preventDefault();
      try {
        window.dispatchEvent(
          new CustomEvent('buildpilot:open-matcher', {
            detail: { category: 'professional:builder' },
          }),
        );
      } catch {
        /* graceful no-op */
      }
    }
  };

  return (
    <Link
      to={`/where-are-you-at/${lane.slug}`}
      onClick={handleClick}
      data-testid={`life-stage-tile-${lane.slug}`}
      className="group relative flex h-full overflow-hidden rounded-2xl transition-all duration-300 sm:hover:-translate-y-1 sm:hover:shadow-2xl flex-row sm:flex-col"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid var(--bp-hairline, #e8ebef)',
        boxShadow: '0 1px 2px rgba(10, 37, 64, 0.04), 0 6px 16px rgba(10, 37, 64, 0.04)',
      }}
    >
      {/* IMAGE
          Mobile (<640px): left-side square thumbnail (~112px)
          Tablet+ (≥640px): full-width magazine hero at 16:10
      */}
      <div
        className="relative flex-shrink-0 overflow-hidden w-[124px] h-[124px] sm:w-full sm:h-auto"
        style={{ aspectRatio: 'auto' }}
      >
        <div className="hidden sm:block w-full" style={{ aspectRatio: '16 / 10' }}>
          <img
            src={lane.image}
            alt={lane.imageAlt || ''}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {/* Bottom gradient wash so the eyebrow lifts off busy shots */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-20"
            style={{
              background: 'linear-gradient(to top, rgba(255,255,255,0.85), rgba(255,255,255,0))',
            }}
          />
          {/* Eyebrow sits over the bottom of the image on tablet/desktop */}
          <div className="absolute bottom-3 left-4 right-4 pointer-events-none">
            <div
              className="text-[11px] font-semibold uppercase"
              style={{
                color: '#0a2540',
                letterSpacing: '0.18em',
                textShadow: '0 1px 2px rgba(255,255,255,0.4)',
              }}
            >
              {lane.eyebrow}
            </div>
          </div>
        </div>
        {/* Mobile-only square thumbnail (no overlay eyebrow - eyebrow
            rides beside the copy on mobile so the tile stays compact) */}
        <div className="sm:hidden w-[124px] h-[124px]">
          <img
            src={lane.image}
            alt={lane.imageAlt || ''}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      {/* COPY
          Mobile: compact, single line title, no long emotion copy.
          Tablet+: full copy with emotion + age whisper. */}
      <div className="flex flex-1 flex-col justify-center min-w-0 px-4 py-3 sm:p-6 lg:p-7">
        {/* Mobile-only eyebrow (above the title) */}
        <div
          className="sm:hidden text-[10px] font-semibold uppercase mb-1"
          style={{ color: '#0a2540', letterSpacing: '0.16em' }}
        >
          {lane.eyebrow}
        </div>

        <h3
          className="text-[15px] sm:text-[19px] lg:text-xl font-semibold leading-snug tracking-tight sm:mb-2"
          style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
        >
          {lane.title}
        </h3>

        {/* Emotion + age whisper - hidden on mobile to reduce scroll,
            visible on tablet+ where the card has room to breathe. */}
        <p
          className="hidden sm:block text-[14.5px] leading-relaxed mb-4"
          style={{ color: '#5a5a5f' }}
        >
          {lane.emotion}
        </p>
        {lane.ageWhisper && (
          <p
            className="hidden sm:block text-xs mb-5"
            style={{ color: 'var(--bp-quiet, #86868b)' }}
          >
            {lane.ageWhisper}
          </p>
        )}

        {/* CTA row - always visible, compacts on mobile */}
        <div
          className="mt-2 sm:mt-auto inline-flex items-center gap-1.5 text-[13px] sm:text-sm font-semibold transition-colors"
          style={{ color: '#0a2540' }}
        >
          <span className="group-hover:text-[#1d8efe] transition-colors truncate">
            {lane.ctaLabel}
          </span>
          <ArrowRight
            className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 transform transition-transform group-hover:translate-x-1 group-hover:text-[#1d8efe]"
            strokeWidth={2.25}
          />
        </div>
      </div>
    </Link>
  );
};

const LifeStageLanesSection = ({ showHeader = true }) => {
  return (
    <section
      className={`relative bg-white ${showHeader ? 'py-12 sm:py-16 lg:py-20 lg:py-24' : 'pb-16 sm:pb-20 lg:pb-24 pt-2 sm:pt-4'}`}
      data-testid="life-stage-lanes-section"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {showHeader && (
          <div className="mb-10 sm:mb-14 max-w-3xl">
            <div
              className="inline-flex items-center gap-2 mb-4 sm:mb-5"
              style={{ color: '#0a2540' }}
            >
              <span
                className="h-px w-6"
                style={{ backgroundColor: '#0a2540' }}
              />
              <span
                className="text-[11px] font-semibold uppercase"
                style={{ letterSpacing: '0.22em' }}
              >
                Where are you at
              </span>
            </div>
            <h2
              className="text-[26px] sm:text-[34px] lg:text-[38px] font-bold leading-[1.15] tracking-tight mb-4"
              style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
            >
              Building a home is a decade-long story, not a one-off transaction.
            </h2>
            <p
              className="text-base sm:text-lg leading-relaxed max-w-2xl"
              style={{ color: '#5a5a5f' }}
            >
              Pick the moment that best describes you today. We have content, tools and human help for every step, from your first pay-cheque to your third home.
            </p>
          </div>
        )}

        {/* Mobile: horizontal compact list (thumbnail + copy) to
            minimise scroll. Tablet: 2-up magazine cards.
            Desktop: 3-up magazine cards. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 lg:gap-7">
          {LIFE_STAGE_LANES.map((lane) => (
            <LifeStageCard key={lane.slug} lane={lane} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LifeStageLanesSection;
